import { LoanOfferItem, LoanOfferProtection, LoanOfferReason, LoanProtectionType } from '@shared/sec-loans'
import { formatFinnishCurrencyWithSymbol } from '@ui-common/utils'
import { IntlShape } from 'react-intl'
import * as R from 'remeda'

interface InstallmentStepData {
  installmentAmount: string
  interestAmount: string
  savingsAmount?: string
  protectionsAmount?: string
  feesAmount: string
  totalAmount: string
  totalAmountWithHigherInterest?: string
  links: Link[]
}

interface Link {
  text: string
  url: string
}

export const getInstallmentData = (offerItem: LoanOfferItem, intl: IntlShape): InstallmentStepData => {
  const applicant = offerItem.applicant
  const getMonthlyMonetaryValue = (monetaryAmount: number) => {
    return `${formatFinnishCurrencyWithSymbol(monetaryAmount)}/${intl.formatMessage({ id: 'common-month-abbreviation' })}`
  }

  const { instalments, interests, paymentProtectionFees, fees, payments, paymentsIncreased } = applicant.monthlyTotals
  const savings = applicant.savings.monthly

  const installmentAmount = getMonthlyMonetaryValue(instalments)
  const interestAmount = getMonthlyMonetaryValue(interests)
  const savingsAmount = savings ? getMonthlyMonetaryValue(savings) : undefined
  const protectionsAmount = paymentProtectionFees ? getMonthlyMonetaryValue(paymentProtectionFees) : undefined
  const feesAmount = getMonthlyMonetaryValue(fees)
  const totalAmount = getMonthlyMonetaryValue(payments)
  const totalAmountWithHigherInterest =
    paymentsIncreased > payments ? getMonthlyMonetaryValue(paymentsIncreased) : undefined

  const links = getLoanProtectionLinks(
    offerItem.offeredLoans.flatMap((o) => (o as LoanOfferReason).protections),
    intl,
  )

  return {
    installmentAmount,
    interestAmount,
    savingsAmount,
    feesAmount,
    protectionsAmount,
    totalAmount,
    totalAmountWithHigherInterest,
    links,
  }
}

const getLoanProtectionLinks = (protections: LoanOfferProtection[], intl: IntlShape) => {
  const commonLinks: Link[] = [
    {
      text: intl.formatMessage({ id: 'loan-offer-loan-protection-brochure-link-text' }),
      url: intl.formatMessage({ id: 'loan-offer-loan-protection-brochure-link-url' }),
    },
    {
      text: intl.formatMessage({ id: 'loan-offer-loan-protection-terms-link-text' }),
      url: intl.formatMessage({ id: 'loan-offer-loan-protection-terms-link-url' }),
    },
  ]

  const productALinks: Link[] = [
    {
      text: intl.formatMessage({ id: 'loan-offer-loan-protection-a-health-clearance-link-text' }),
      url: intl.formatMessage({ id: 'loan-offer-loan-protection-a-health-clearance-link-url' }),
    },
  ]

  const productBLinks: Link[] = [
    {
      text: intl.formatMessage({ id: 'loan-offer-loan-protection-b-health-clearance-link-text' }),
      url: intl.formatMessage({ id: 'loan-offer-loan-protection-b-health-clearance-link-url' }),
    },
    {
      text: intl.formatMessage({ id: 'loan-offer-loan-protection-b-brochure-link-text' }),
      url: intl.formatMessage({ id: 'loan-offer-loan-protection-b-brochure-link-url' }),
    },
  ]

  const loanProtectionLinks: Record<LoanProtectionType, Link[]> = {
    [LoanProtectionType.A]: [...productALinks, ...commonLinks],
    [LoanProtectionType.B]: [...productBLinks, ...commonLinks],
    [LoanProtectionType.B2]: [...productBLinks, ...commonLinks],
    [LoanProtectionType.AB]: [...productALinks, ...productBLinks, ...commonLinks],
    [LoanProtectionType.AB2]: [...productALinks, ...productBLinks, ...commonLinks],
  }

  return R.pipe(
    protections,
    R.flatMap((p) => loanProtectionLinks[p.type]),
    R.uniqueBy((l) => l.url),
  )
}
